import { useEffect, useRef } from "react";

import { css } from "@compiled/react";

import { wacToken } from "@/libraries/design/foundations";

const SQUARE = 2; // square edge (px)
const SPACING = 16; // lattice pitch — dots sit 16px apart
const REST_RGB = [0x4b, 0x4d, 0x51] as const; // #4B4D51
const REST_ALPHA = 0.4; // 40% opacity at rest
const WAKE_RGB_DARK = [255, 255, 255] as const; // displaced dots brighten to white

const HOVER_RADIUS = 130; // repulsion reach (px)
const PUSH_MAX = 56; // strongest displacement, right at the cursor (px)
const OPACITY_SCALE = 26; // displacement (px) at which a dot reaches full opacity

const STIFFNESS = 0.055; // pull toward the target offset
const DAMPING = 0.6; // velocity retained each frame (<1)
const SETTLE = 0.25; // offset/velocity threshold to retire a spring

type Spring = {
    rx: number; // rest position
    ry: number;
    ox: number; // current offset from rest
    oy: number;
    vx: number; // offset velocity
    vy: number;
    tx: number; // target offset (repulsion)
    ty: number;
    touched: number; // frame id this spring was last within reach
};

const backgroundCss = css({
    "position": "absolute",
    "inset": 0,
    "width": "100%",
    "height": "100%",
    "pointerEvents": "none",
    "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        background: wacToken("color.neutral.dark100")
    }
});

const MASK_MOBILE =
    // eslint-disable-next-line local/ensure-wac-token-usage
    "linear-gradient(180deg, transparent 0, #000 160px, #000 calc(100% - 160px), transparent 100%)";
const MASK_TABLET =
    // eslint-disable-next-line local/ensure-wac-token-usage
    "linear-gradient(180deg, transparent 0, #000 220px, #000 calc(100% - 220px), transparent 100%)";

const canvasCss = css({
    "position": "absolute",
    "inset": 0,
    "width": "100%",
    "height": "100%",
    "pointerEvents": "none",
    "WebkitMaskImage": MASK_MOBILE,
    "maskImage": MASK_MOBILE,
    "@media (min-width: 768px)": {
        WebkitMaskImage: MASK_TABLET,
        maskImage: MASK_TABLET
    }
});

const UniformPixelRepelGrid = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        const parent = canvas.parentElement;
        if (!parent) return;

        const rest = REST_RGB;
        const restAlpha = REST_ALPHA;
        const wake = WAKE_RGB_DARK;

        // Offscreen layer holding the resting dot field (drawn once per resize).
        const layer = document.createElement("canvas");
        const layerCtx = layer.getContext("2d");
        if (!layerCtx) return;

        let width = 0;
        let height = 0;
        let cols = 0;
        let rows = 0;
        let offsetX = 0;
        let offsetY = 0;
        let dpr = 1;

        let raf = 0;
        let frameId = 0;
        let visible = true;
        let running = false;

        // Live springs keyed by lattice index. A cell only gets a spring while it
        // is within reach of the cursor or still settling back to rest afterwards.
        const springs = new Map<number, Spring>();

        // Pointer kept in CSS px relative to the canvas. We keep the last known
        // position after the pointer leaves so springs relax from where they were.
        let mouseX = -9999;
        let mouseY = -9999;
        let pointerOver = false;

        function drawStatic() {
            ctx?.clearRect(0, 0, width, height);
            if (layer.width === 0 || layer.height === 0) return;
            ctx?.drawImage(layer, 0, 0, width, height);
        }

        function build() {
            if (!parent || !canvas || !ctx || !layerCtx) return;

            const rect = parent.getBoundingClientRect();
            width = rect.width;
            height = rect.height;
            // Bail while the parent has no layout box yet (e.g. hidden/0-size).
            // Sizing the canvases to 0 makes drawImage throw on the empty layer.
            if (width <= 0 || height <= 0) return;
            dpr = Math.max(1, window.devicePixelRatio || 1);

            canvas.width = Math.round(width * dpr);
            canvas.height = Math.round(height * dpr);
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

            layer.width = canvas.width;
            layer.height = canvas.height;
            layerCtx.setTransform(dpr, 0, 0, dpr, 0, 0);

            cols = Math.ceil(width / SPACING) + 1;
            rows = Math.ceil(height / SPACING) + 1;
            offsetX = (width - (cols - 1) * SPACING) / 2;
            offsetY = SPACING / 2;

            // Pre-render the resting field once.
            layerCtx.clearRect(0, 0, width, height);
            layerCtx.fillStyle = `rgba(${rest[0]}, ${rest[1]}, ${rest[2]}, ${restAlpha})`;
            const half = SQUARE / 2;
            for (let gy = 0; gy < rows; gy++) {
                const ry = offsetY + gy * SPACING;
                for (let gx = 0; gx < cols; gx++) {
                    const rx = offsetX + gx * SPACING;
                    layerCtx.fillRect(rx - half, ry - half, SQUARE, SQUARE);
                }
            }

            // Rest positions changed — drop stale springs and repaint clean.
            springs.clear();
            drawStatic();
        }

        function frame() {
            frameId++;

            // 1. Refresh repulsion targets for every cell currently within reach.
            if (pointerOver) {
                const gxMin = Math.max(
                    0,
                    Math.floor((mouseX - HOVER_RADIUS - offsetX) / SPACING)
                );
                const gxMax = Math.min(
                    cols - 1,
                    Math.ceil((mouseX + HOVER_RADIUS - offsetX) / SPACING)
                );
                const gyMin = Math.max(
                    0,
                    Math.floor((mouseY - HOVER_RADIUS - offsetY) / SPACING)
                );
                const gyMax = Math.min(
                    rows - 1,
                    Math.ceil((mouseY + HOVER_RADIUS - offsetY) / SPACING)
                );

                for (let gy = gyMin; gy <= gyMax; gy++) {
                    const ry = offsetY + gy * SPACING;
                    for (let gx = gxMin; gx <= gxMax; gx++) {
                        const rx = offsetX + gx * SPACING;
                        const dx = rx - mouseX; // points away from the cursor
                        const dy = ry - mouseY;
                        const d = Math.hypot(dx, dy);
                        if (d >= HOVER_RADIUS) continue;

                        const key = gy * cols + gx;
                        let s = springs.get(key);
                        if (!s) {
                            s = {
                                rx,
                                ry,
                                ox: 0,
                                oy: 0,
                                vx: 0,
                                vy: 0,
                                tx: 0,
                                ty: 0,
                                touched: 0
                            };
                            springs.set(key, s);
                        }

                        if (d > 0.001) {
                            // Soft repulsion: strongest at the cursor, easing to
                            // nothing at the rim. Squared falloff keeps the near
                            // field punchy and the outer field gentle, so the
                            // dots part organically.
                            const f = 1 - d / HOVER_RADIUS;
                            const push = PUSH_MAX * f * f;
                            const inv = push / d;
                            s.tx = dx * inv;
                            s.ty = dy * inv;
                        } else {
                            s.tx = 0;
                            s.ty = 0;
                        }
                        s.touched = frameId;
                    }
                }
            }

            // 2. Integrate every spring. Cells not refreshed this frame relax to
            //    rest.
            const dead: number[] = [];
            springs.forEach((s, key) => {
                if (s.touched !== frameId) {
                    s.tx = 0;
                    s.ty = 0;
                }
                s.vx = s.vx * DAMPING + (s.tx - s.ox) * STIFFNESS;
                s.vy = s.vy * DAMPING + (s.ty - s.oy) * STIFFNESS;
                s.ox += s.vx;
                s.oy += s.vy;

                if (
                    s.touched !== frameId &&
                    Math.abs(s.ox) < SETTLE &&
                    Math.abs(s.oy) < SETTLE &&
                    Math.abs(s.vx) < SETTLE &&
                    Math.abs(s.vy) < SETTLE
                ) {
                    dead.push(key);
                }
            });

            // 3. Render: blit the resting field, lift each displaced dot off its
            //    rest position, then paint it at higher opacity in proportion to
            //    how far it moved (same grey, just from rest up to full wake
            //    colour).
            drawStatic();

            const half = SQUARE / 2;
            const clearPad = SQUARE / 2 + 0.5;
            const clearSize = SQUARE + 1;
            springs.forEach((s) => {
                ctx?.clearRect(
                    s.rx - clearPad,
                    s.ry - clearPad,
                    clearSize,
                    clearSize
                );
            });
            springs.forEach((s) => {
                if (!ctx) return;

                const mag = Math.hypot(s.ox, s.oy);
                const w = mag < OPACITY_SCALE ? mag / OPACITY_SCALE : 1;
                const rr = rest[0] + (wake[0] - rest[0]) * w;
                const gg = rest[1] + (wake[1] - rest[1]) * w;
                const bb = rest[2] + (wake[2] - rest[2]) * w;
                ctx.fillStyle = `rgba(${rr | 0}, ${gg | 0}, ${
                    bb | 0
                }, ${restAlpha})`;
                ctx.fillRect(
                    s.rx + s.ox - half,
                    s.ry + s.oy - half,
                    SQUARE,
                    SQUARE
                );
            });

            for (const deadKey of dead) springs.delete(deadKey as number);

            if (visible && (pointerOver || springs.size > 0)) {
                raf = requestAnimationFrame(frame);
            } else {
                running = false;
                if (visible) drawStatic(); // settle on the clean resting field
            }
        }

        function start() {
            if (!running && visible) {
                running = true;
                raf = requestAnimationFrame(frame);
            }
        }

        const onPointerMove = (e: PointerEvent) => {
            const rect = canvas.getBoundingClientRect();
            mouseX = e.clientX - rect.left;
            mouseY = e.clientY - rect.top;
            pointerOver = true;
            start();
        };
        const onPointerLeave = () => {
            pointerOver = false;
            start();
        };

        build();
        const ro = new ResizeObserver(build);
        ro.observe(parent);

        // Pause entirely while the parent is scrolled out of view.
        const io = new IntersectionObserver(
            ([entry]) => {
                visible = !!entry?.isIntersecting;
                if (visible) {
                    drawStatic();
                    if (pointerOver || springs.size > 0) start();
                }
            },
            { threshold: 0 }
        );
        io.observe(parent);

        parent.addEventListener("pointermove", onPointerMove);
        parent.addEventListener("pointerleave", onPointerLeave);

        return () => {
            cancelAnimationFrame(raf);
            running = false;
            ro.disconnect();
            io.disconnect();
            parent.removeEventListener("pointermove", onPointerMove);
            parent.removeEventListener("pointerleave", onPointerLeave);
        };
    }, []);

    return (
        <>
            <div css={backgroundCss} />
            <canvas ref={canvasRef} css={canvasCss} />
        </>
    );
};

export default UniformPixelRepelGrid;
